import { Injectable } from '@angular/core';
import { TokenStorageService } from './token-storage.service';
import { ModuleName, Permission } from '../models/permission';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  
  constructor(private tokenStorage: TokenStorageService) {}

  hasModule(module: ModuleName | string): boolean {
    const userModules = this.tokenStorage.getUserModules();
    return userModules.includes(module);
  }

  hasAnyModule(modules: (ModuleName | string)[]): boolean {
    if (!modules || modules.length === 0) return false;
    return modules.some(module => this.hasModule(module));
  }

  hasAllModules(modules: (ModuleName | string)[]): boolean {
    if (!modules || modules.length === 0) return true;
    return modules.every(module => this.hasModule(module));
  }

  hasPermission(module: string, action: string): boolean {
    const permissions = this.tokenStorage.getUserPermissions();
    if (!permissions || !permissions[module]) return false;
    
    // Handle both formats:
    // 1. Just action: 'CREATE', 'UPDATE', 'VIEW'
    // 2. Full format: 'MODULE:ACTION'
    return permissions[module].some(p => {
      if (typeof p === 'string') {
        // Check if it's just the action name
        if (p === action) return true;
        const permissionCode = `${module}:${action}`;
        return p === permissionCode || p === `CHURCH|${permissionCode}`;
      }
      return p.action === action ||
        p.code === `${module}:${action}` ||
        p.code === `CHURCH|${module}:${action}`;
    });
  }

  /** Checks a permission code such as `ROLE_MANAGEMENT:VIEW`. */
  hasPermissionCode(permissionCode: string): boolean {
    if (!permissionCode) return false;

    const permissions = this.tokenStorage.getUserPermissions();
    return Object.values(permissions || {}).some((modulePermissions: any[]) =>
      Array.isArray(modulePermissions) && modulePermissions.some((permission) =>
        typeof permission === 'string'
          ? permission === permissionCode || permission === `CHURCH|${permissionCode}`
          : permission?.code === permissionCode ||
            permission?.code === `CHURCH|${permissionCode}`
      )
    );
  }

  hasAnyPermissionInModule(module: string): boolean {
    const permissions = this.tokenStorage.getUserPermissions();
    return permissions && permissions[module] && permissions[module].length > 0;
  }

  getUserModules(): string[] {
    return this.tokenStorage.getUserModules();
  }

  getUserPermissions(): { [module: string]: Permission[] } {
    return this.tokenStorage.getUserPermissions();
  }

  getModulePermissions(module: string): Permission[] {
    const permissions = this.tokenStorage.getUserPermissions();
    return permissions?.[module] || [];
  }

  getFirstAccessibleRoute(routes: any[]): string | null {
    for (const route of routes) {
      if (route.groupTitle) continue;
      
      if (route.modules && route.modules.length > 0) {
        const hasAccess = route.requireAllModules 
          ? this.hasAllModules(route.modules)
          : this.hasAnyModule(route.modules);
        
        if (hasAccess) {
          if (route.path && route.path !== '') {
            return route.path;
          }
          
          if (route.submenu && route.submenu.length > 0) {
            const submenuRoute = this.getFirstAccessibleRoute(route.submenu);
            if (submenuRoute) return submenuRoute;
          }
        }
      }
    }
    return null;
  }
}
